"use client";

import React, { useEffect, useState } from "react";
import { Menu as MenuIcon, MessageCircle, Search, Sparkles, User, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { CartIcon } from "@/components/shop/CartIcon";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { type MenuItem, MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { useWooAccountUrl } from "@/integrations/wordpress/useWooAccountUrl";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
interface RecursiveSubmenuProps {
  items: MenuItem[];
  level?: number;
}
function DesktopRecursiveSubmenu({
  items,
  level = 0
}: RecursiveSubmenuProps) {
  if (!items || items.length === 0) return null;
  return <ul className={`grid min-w-[200px] gap-1.5 p-1 ${level > 0 ? "pl-3 border-l border-border/60 ml-2" : ""}`}>
      {items.map((child, i) => <li key={child.id} data-index={i} className="flex flex-col gap-1">
          <NavigationMenuLink asChild>
            <Link to={child.href || "#"} className="block select-none rounded-md px-3 py-2 text-xs font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
              {child.label}
            </Link>
          </NavigationMenuLink>
          {child.children && child.children.length > 0 && <DesktopRecursiveSubmenu items={child.children} level={level + 1} />}
        </li>)}
    </ul>;
}
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex items-center gap-3">
        <div className="h-8 w-16 animate-pulse rounded-full bg-muted" />
        <div className="h-8 w-16 animate-pulse rounded-full bg-muted" />
        <div className="h-8 w-20 animate-pulse rounded-full bg-muted" />
        <div className="h-8 w-18 animate-pulse rounded-full bg-muted" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="h-9 rounded-full bg-transparent px-4 text-xs font-semibold tracking-wide text-foreground transition-colors hover:bg-accent hover:text-accent-foreground data-[state=open]:bg-accent/70">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent className="rounded-xl border border-border bg-popover p-2 shadow-md">
                  <DesktopRecursiveSubmenu items={item.children} />
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="inline-flex h-9 items-center justify-center rounded-full px-4 text-xs font-semibold tracking-wide text-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                  {item.label}
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileRecursiveMenu({
  items,
  level = 0,
  onItemClick
}: {
  items: MenuItem[];
  level?: number;
  onItemClick?: () => void;
}) {
  if (!items || items.length === 0) return null;
  return <ul className={`flex flex-col gap-1 ${level > 0 ? "mt-1 pl-4 border-l border-border/60" : ""}`}>
      {items.map((item, i) => <li key={item.id} data-index={i} className="flex flex-col">
          <SheetClose asChild>
            <Link to={item.href || "#"} onClick={onItemClick} className="flex items-center justify-between py-2.5 text-sm font-medium text-foreground transition-colors hover:text-primary">
              <span>{item.label}</span>
            </Link>
          </SheetClose>
          {item.children && item.children.length > 0 && <MobileRecursiveMenu items={item.children} level={level + 1} onItemClick={onItemClick} />}
        </li>)}
    </ul>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose?: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3 py-4">
        <div className="h-7 w-3/4 animate-pulse rounded-md bg-muted" />
        <div className="h-7 w-2/3 animate-pulse rounded-md bg-muted" />
        <div className="h-7 w-4/5 animate-pulse rounded-md bg-muted" />
        <div className="h-7 w-1/2 animate-pulse rounded-md bg-muted" />
      </div>;
  }
  return <nav aria-label="Navegación móvil" className="flex flex-col">
      <MobileRecursiveMenu items={menuItems} onItemClick={onClose} />
    </nav>;
}
export default function Header() {
  const accountUrl = useWooAccountUrl();
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setIsSearchOpen(false);
    }
  };
  return <section id="header" data-nav="light" className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur-md transition-colors duration-200">
      <div className="mx-auto flex h-20 max-w-[1240px] items-center justify-between px-5 md:px-8 lg:px-14">
        {/* Brand Logo */}
        <div className="flex shrink-0 items-center">
          <Link to="/" className="inline-flex items-center rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" aria-label="Spora Inicio">
            <WvcLogo className="h-10 w-auto" />
          </Link>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex lg:items-center lg:justify-center">
          <MenuProvider menu_id="22">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {/* WhatsApp Desktop Link */}
        <Link to="https://wa.me/5639561232" newTab aria-label="Contactar a Spora por WhatsApp" className="hidden lg:flex size-9 items-center justify-center rounded-full text-foreground hover:bg-accent hover:text-accent-foreground">
          <MessageCircle className="size-4" />
        </Link>

        {/* Utility Actions Bar */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          {/* Quick Search Popover */}
          <Popover open={isSearchOpen} onOpenChange={setIsSearchOpen}>
            <PopoverTrigger asChild>
              <Button type="button" variant="ghost" size="icon" className="size-9 rounded-full text-foreground hover:bg-accent hover:text-accent-foreground" aria-label="Buscar rituales y productos">
                <Search className="size-4" aria-hidden="true" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" sideOffset={8} className="w-80 rounded-2xl border border-border bg-popover p-4 shadow-lg">
              <form onSubmit={handleSearchSubmit} className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Buscar en Spora
                  </span>
                  <Sparkles className="size-3.5 text-primary" aria-hidden="true" />
                </div>
                <div className="relative flex items-center">
                  <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Reishi, Maitake, Crema, Té..." className="h-10 w-full rounded-xl border border-input bg-card px-3.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
                  {searchQuery && <Button type="button" variant="ghost" size="icon" onClick={() => setSearchQuery("")} className="absolute right-1 size-8 rounded-full p-0 text-muted-foreground hover:text-foreground">
                      <X className="size-3.5" />
                    </Button>}
                </div>
                <Button type="submit" size="sm" className="w-full rounded-full bg-primary font-medium text-primary-foreground shadow-xs hover:bg-primary/90">
                  Buscar productos
                </Button>
              </form>
            </PopoverContent>
          </Popover>

          {/* User Account Link */}
          <Button asChild variant="ghost" size="icon" className="size-9 rounded-full text-foreground hover:bg-accent hover:text-accent-foreground">
            <Link to={accountUrl} aria-label="Mi Cuenta">
              <User className="size-4" aria-hidden="true" />
            </Link>
          </Button>

          {/* Cart Trigger */}
          <CartIcon iconType="bag" variant="ghost" size="icon" iconSize="size-4" className="rounded-full text-foreground hover:bg-accent hover:text-accent-foreground" openDrawerOnAdd={true} />

          {/* Mobile Hamburger Drawer Trigger */}
          <div className="flex lg:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button type="button" variant="ghost" size="icon" className="size-9 rounded-full text-foreground hover:bg-accent hover:text-accent-foreground" aria-label="Abrir menú de navegación">
                  <MenuIcon className="size-5" aria-hidden="true" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="flex w-full max-w-xs flex-col justify-between border-l border-border bg-background p-6">
                <div className="flex flex-col gap-6">
                  <SheetHeader className="p-0 text-left">
                    <SheetTitle className="sr-only">Menú de navegación</SheetTitle>
                    <SheetDescription className="sr-only">
                      Acceso a secciones de tienda, rituales botánicos y contacto.
                    </SheetDescription>
                    <div className="flex items-center pb-2 pt-1">
                      <Link to="/" aria-label="Spora Inicio">
                        <WvcLogo className="h-8 w-auto" />
                      </Link>
                    </div>
                  </SheetHeader>

                  <div className="py-2">
                    <MenuProvider menu_id="22">
                      <MobileMenuConsumer />
                    </MenuProvider>
                  </div>
                </div>

                {/* Mobile Drawer Bottom Info */}
                <div className="border-t border-border pt-6">
                  <div className="flex flex-col gap-3">
                    <Button asChild variant="outline" className="w-full justify-center rounded-full border-border bg-card py-2 text-xs font-semibold text-foreground hover:bg-accent">
                      <Link to={accountUrl}>
                        <User className="mr-2 size-3.5" />
                        Mi Cuenta
                      </Link>
                    </Button>
                    <p className="text-center text-[11px] leading-relaxed text-muted-foreground">
                      Rituales de hongos funcionales para rostro, cuerpo e interior.
                    </p>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}